/**
 * Calendar range engine.
 *
 * Pure functions only: no React, no fetching, no DOM, no Date arithmetic of
 * our own. Every date is a naive 'YYYY-MM-DD' string. All arithmetic is
 * delegated to the tested helpers in ./time.ts.
 *
 * The day/week/month calendar UI sits on top of this module so the logic can
 * be tested exhaustively in isolation.
 */
import {
  addDays,
  addMonths,
  startOfMonth,
  startOfWeek,
  dayOfWeek,
  compareDates,
  isCalendarDate,
} from './time';
import type { CalendarDate } from './time';

export type CalendarResolution = 'day' | 'week' | 'month';

export type CalendarRange = { start: CalendarDate; end: CalendarDate };

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Zero-padded 2-digit month number ('01'..'12') -> 1-based month index. */
function monthIndex(date: CalendarDate): number {
  return Number(date.slice(5, 7));
}

/** Zero-padded 2-digit day number ('01'..'31') -> 1-based day index. */
function dayIndex(date: CalendarDate): number {
  return Number(date.slice(8, 10));
}

/** 4-digit year as a number. */
function yearOf(date: CalendarDate): number {
  return Number(date.slice(0, 4));
}

/** Last day of the month containing `date`, as a CalendarDate. */
function lastDayOfMonth(date: CalendarDate): CalendarDate {
  // First day of the NEXT month, then step back one day. This reuses addMonths
  // (which clamps) and addDays, so we never do Date arithmetic ourselves.
  const firstOfNext = startOfMonth(addMonths(date, 1));
  return addDays(firstOfNext, -1);
}

/**
 * The inclusive date range covering the anchor at the given resolution.
 *
 * - day   -> { start: anchor, end: anchor }
 * - week  -> Sunday..Saturday containing the anchor
 * - month -> the 1st..last day of the anchor's month
 */
export function rangeFor(resolution: CalendarResolution, anchor: CalendarDate): CalendarRange {
  if (!isCalendarDate(anchor)) throw new Error(`anchor must be YYYY-MM-DD, got "${anchor}"`);
  switch (resolution) {
    case 'day':
      return { start: anchor, end: anchor };
    case 'week': {
      const start = startOfWeek(anchor);
      const end = addDays(start, 6);
      return { start, end };
    }
    case 'month': {
      const start = startOfMonth(anchor);
      const end = lastDayOfMonth(anchor);
      return { start, end };
    }
  }
}

/**
 * The dates a UI should render, in order.
 *
 * - day   -> exactly the anchor
 * - week  -> the 7 days Sunday..Saturday
 * - month -> COMPLETE WEEKS covering the month: starts on the Sunday on or
 *            before the 1st and ends on the Saturday on or after the last day, so
 *            the length is always a multiple of 7. Leading and trailing days
 *            from neighbouring months ARE included.
 */
export function gridFor(resolution: CalendarResolution, anchor: CalendarDate): CalendarDate[] {
  if (!isCalendarDate(anchor)) throw new Error(`anchor must be YYYY-MM-DD, got "${anchor}"`);
  switch (resolution) {
    case 'day':
      return [anchor];
    case 'week': {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    case 'month': {
      const first = startOfMonth(anchor);
      const last = lastDayOfMonth(anchor);
      // Monday on or before the 1st.
      const start = startOfWeek(first);
      // Sunday on or after the last day: the week containing `last` ends on its
      // Sunday, which is startOfWeek(last) + 6.
      const end = addDays(startOfWeek(last), 6);
      const days = [];
      for (let d = start; compareDates(d, end) <= 0; d = addDays(d, 1)) {
        days.push(d);
      }
      return days;
    }
  }
}

/**
 * True when a grid cell belongs to a neighbouring month, so the UI can dim it.
 *
 * A cell is "outside" the anchor's month when its month differs from the
 * anchor's month. This is only meaningful for month grids (where leading and
 * trailing cells come from adjacent months); for day/week grids it is always
 * false because every cell is within the anchor's month or week.
 */
export function isOutsideMonth(date: CalendarDate, anchor: CalendarDate): boolean {
  if (!isCalendarDate(date)) throw new Error(`date must be YYYY-MM-DD, got "${date}"`);
  if (!isCalendarDate(anchor)) throw new Error(`anchor must be YYYY-MM-DD, got "${anchor}"`);
  return monthIndex(date) !== monthIndex(anchor) || yearOf(date) !== yearOf(anchor);
}

/**
 * Move by whole segments. delta is typically -1 or +1.
 *
 * - day   -> addDays(anchor, delta)
 * - week  -> addDays(anchor, delta * 7)
 * - month -> addMonths(anchor, delta)
 *
 * NOTE on month clamping: addMonths clamps, so 31 January + 1 month is
 * 28/29 February, not 3 March. This means month navigation is NOT reversible
 * from a clamped date: forward from Jan 31 lands on Feb 28, and back from
 * Feb 28 lands on Jan 28, losing the 31st. This is deliberate and tested.
 */
export function navigate(
  resolution: CalendarResolution,
  anchor: CalendarDate,
  delta: number
): CalendarDate {
  if (!isCalendarDate(anchor)) throw new Error(`anchor must be YYYY-MM-DD, got "${anchor}"`);
  switch (resolution) {
    case 'day':
      return addDays(anchor, delta);
    case 'week':
      return addDays(anchor, delta * 7);
    case 'month':
      return addMonths(anchor, delta);
  }
}

/**
 * A human label for the current segment, built from the date STRING parts, not
 * from Intl on a Date object.
 *
 * - day   -> 'Tuesday, 15 September 2026'
 * - week  -> '14 – 20 September 2026' (spanning a month/year boundary reads
 *            sensibly, e.g. '28 September – 4 October 2026' and
 *            '29 December 2025 – 4 January 2026')
 * - month -> 'September 2026'
 */
export function formatRangeLabel(resolution: CalendarResolution, anchor: CalendarDate): string {
  if (!isCalendarDate(anchor)) throw new Error(`anchor must be YYYY-MM-DD, got "${anchor}"`);
  switch (resolution) {
    case 'day': {
      const dow = dayOfWeek(anchor);
      return `${DAY_NAMES[dow]}, ${dayIndex(anchor)} ${MONTH_NAMES[monthIndex(anchor) - 1]} ${yearOf(anchor)}`;
    }
    case 'week': {
      const { start, end } = rangeFor('week', anchor);
      // Same month and year: show the month/year once.
      //   '14 – 20 September 2026'
      // Same year, different months: show each day's month, year once at end.
      //   '28 September – 4 October 2026'
      // Different years: show each day fully.
      //   '29 December 2025 – 4 January 2026'
      if (monthIndex(start) === monthIndex(end) && yearOf(start) === yearOf(end)) {
        return `${dayIndex(start)} – ${dayIndex(end)} ${MONTH_NAMES[monthIndex(start) - 1]} ${yearOf(start)}`;
      }
      if (yearOf(start) === yearOf(end)) {
        return `${dayIndex(start)} ${MONTH_NAMES[monthIndex(start) - 1]} – ${dayIndex(end)} ${MONTH_NAMES[monthIndex(end) - 1]} ${yearOf(start)}`;
      }
      return `${formatDay(start)} – ${formatDay(end)}`;
    }
    case 'month':
      return `${MONTH_NAMES[monthIndex(anchor) - 1]} ${yearOf(anchor)}`;
  }
}

/** '15 September 2026' — day, month name, year, from string parts only. */
function formatDay(date: CalendarDate): string {
  return `${dayIndex(date)} ${MONTH_NAMES[monthIndex(date) - 1]} ${yearOf(date)}`;
}
